import { cn } from '@/shared/lib/cn';

import { cardBrandOf, type CardBrand } from './cardBrand';

/**
 * Network wordmarks, set in white.
 *
 * Deliberately NOT the licensed logo artwork. Every network's real mark carries
 * its own brand colours — Visa's blue, Amex's blue — which disappear against a
 * dark card face, and the usual fix of printing them on a white plate makes the
 * face look like a sticker album. White type reads cleanly on all eight faces
 * and keeps each card's colour doing the work of telling them apart.
 *
 * The letterforms are approximations styled with weight, case and tracking, not
 * reproductions of the brands' own typefaces.
 */

interface BrandWordmark {
  text: string;
  className: string;
}

const WORDMARKS: Record<CardBrand, BrandWordmark> = {
  visa: { text: 'VISA', className: 'text-[17px] font-black italic tracking-[0.06em]' },
  mastercard: { text: 'mastercard', className: 'text-[13px] font-semibold tracking-tight' },
  amex: { text: 'AMEX', className: 'text-[15px] font-extrabold tracking-[0.12em]' },
  discover: { text: 'DISCOVER', className: 'text-[11px] font-bold tracking-[0.08em]' },
  unionpay: { text: 'UnionPay', className: 'text-[14px] font-bold tracking-tight' },
  jcb: { text: 'JCB', className: 'text-[16px] font-extrabold tracking-[0.1em]' },
  diners: { text: 'DINERS', className: 'text-[12px] font-bold tracking-[0.08em]' },
  unknown: { text: 'CARD', className: 'text-[12px] font-bold tracking-[0.08em]' },
};

interface CardBrandMarkProps {
  brand: string;
  className?: string;
}

export const CardBrandMark = ({ brand, className }: CardBrandMarkProps) => {
  const wordmark = WORDMARKS[cardBrandOf(brand)];

  // Decorative: the card's accessible name already states the network, so the
  // mark would otherwise be announced twice.
  return (
    <span
      aria-hidden
      className={cn('leading-none whitespace-nowrap text-white', wordmark.className, className)}
    >
      {wordmark.text}
    </span>
  );
};
